/**
 * ולידציית תעודת זהות ישראלית
 * משתמש באלגוריתם Luhn מותאם של מדינת ישראל
 */

/**
 * בודקת האם הקלט הוא מספר ת.ז. ישראלי תקין.
 * מחזירה true אך ורק עבור ת.ז. תקינה (9 ספרות + ספרת ביקורת).
 * כל קלט אחר (ריק, רווחים בלבד, תווים שאינם ספרות, אורך שגוי, ספרת ביקורת שגויה) -> false.
 * trim מסיר רווחים מהתחלה/סוף בלבד; תווים באמצע יידחו ע"י ה-regex.
 */
export const validateIsraeliId = (id: string): boolean => {
  if (!id) return false;

  const trimmed = id.trim();

  // ת.ז. ישראלית: בדיוק 9 ספרות ASCII (0-9), ללא תווים נוספים
  if (!/^\d{9}$/.test(trimmed)) return false;

  // אלגוריתם ספרת הביקורת
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    const digit = parseInt(trimmed[i], 10);
    const multiplier = (i % 2) + 1; // 1, 2, 1, 2, 1, 2, 1, 2, 1
    const step = digit * multiplier;
    sum += step > 9 ? step - 9 : step;
  }

  return sum % 10 === 0;
};

/**
 * בדיקת תקינות פורמט של שדה ת.ז. עם תמיכה בתושב חו"ל.
 * מחזירה הודעת שגיאה ספציפית (לפי סוג הבעיה) או null אם הקלט תקין/לא הוזן.
 *
 * הערה: בדיקת "שדה חובה" אינה כאן - הקורא בודק זאת בנפרד ומציג "יש להזין ת.ז."
 * *לפני* שמגיע לפונקציה זו. הסיבה: LeadsController מריץ ולידציה על 5 שדות ת.ז. של ליד,
 * וחלקם רלוונטיים רק לפי סוג הקצבה ולכן יכולים להגיע ריקים באופן לגיטימי.
 */
export const validateIdField = (id: string | undefined, isForeignResident: boolean): string | null => {
  // תושב חו"ל - אין ולידציה
  if (isForeignResident) return null;

  // ערך לא הוזן בכלל (null / undefined / "") - לא נכנסים לבדיקת פורמט
  if (!id) return null;

  const trimmed = id.trim();

  // אורך שגוי (כולל המקרה של "רווחים בלבד" שאחרי trim הופך ל-"")
  if (trimmed.length !== 9) {
    return 'תעודת זהות חייבת להכיל בדיוק 9 ספרות';
  }
  // אורך 9 אך עם תווים שאינם ספרות (למשל "12345-678", "12345 678", "abcdefghi")
  if (!/^\d{9}$/.test(trimmed)) {
    return 'תעודת זהות יכולה להכיל ספרות בלבד';
  }
  // 9 ספרות אך ספרת ביקורת שגויה
  if (!validateIsraeliId(trimmed)) {
    return 'מספר תעודת זהות לא תקין';
  }

  return null;
};
